import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../../lib/utils";

// Escurece a foto da esquerda para a direita (estilo inline: a classe bg-gradient-to-r
// tem uma animação global em App.css que não serve para este overlay)
const OVERLAY_STYLE = {
  backgroundImage:
    "linear-gradient(90deg, rgba(12,10,9,0.95) 0%, rgba(12,10,9,0.8) 45%, rgba(12,10,9,0.15) 100%)",
};

/**
 * Hero com banners rotativos (layout docs/tela). Controlado por FEATURES.SHOW_BANNER_CAROUSEL.
 *
 * @param {Array} banners - Array de objetos com { id, title, subtitle, image, cta, badge? }
 * @param {function} onCtaClick - Ação do botão (ex.: rolar até os produtos)
 */
export const BannerCarousel = ({ banners = [], onCtaClick = () => {} }) => {
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);

  // Auto-rotate banner a cada 5 segundos
  useEffect(() => {
    if (banners.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentBannerIndex((prev) => (prev + 1) % banners.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [banners.length]);

  if (banners.length === 0) {
    return null;
  }

  const nextBanner = () => {
    setCurrentBannerIndex((prev) => (prev + 1) % banners.length);
  };

  const prevBanner = () => {
    setCurrentBannerIndex(
      (prev) => (prev - 1 + banners.length) % banners.length
    );
  };

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8">
      <div className="relative overflow-hidden rounded-3xl bg-stone-950 text-white shadow-2xl border border-stone-800 min-h-[300px] sm:min-h-[360px] lg:min-h-[380px]">
        {banners.map((banner, index) => (
          <div
            key={banner.id}
            aria-hidden={index !== currentBannerIndex}
            className={cn(
              "absolute inset-0 flex items-center transition-opacity duration-700",
              index === currentBannerIndex
                ? "opacity-100"
                : "opacity-0 pointer-events-none"
            )}
          >
            <img
              src={banner.image}
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-50"
            />
            <div className="absolute inset-0" style={OVERLAY_STYLE} />
            <div className="relative z-10 p-6 sm:p-10 lg:p-12 max-w-2xl space-y-3 sm:space-y-4">
              {banner.badge && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-extrabold uppercase tracking-wider bg-brand-accent text-brand-on-accent">
                  {banner.badge}
                </span>
              )}
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
                {banner.title}
              </h2>
              <p className="text-stone-300 text-sm sm:text-base leading-relaxed max-w-xl">
                {banner.subtitle}
              </p>
              {banner.cta && (
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={onCtaClick}
                    tabIndex={index === currentBannerIndex ? 0 : -1}
                    className="inline-flex items-center gap-2 min-h-[48px] bg-brand-600 hover:bg-brand-700 active:scale-95 text-white font-extrabold text-xs sm:text-sm uppercase tracking-wider px-6 rounded-2xl shadow-float transition"
                  >
                    {banner.cta} →
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}

        {banners.length > 1 && (
          <>
            {/* Setas (tablet/desktop) */}
            <button
              type="button"
              onClick={prevBanner}
              aria-label="Banner anterior"
              className="hidden sm:flex absolute left-3 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center bg-white/15 hover:bg-white/30 text-white rounded-full transition-colors"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              type="button"
              onClick={nextBanner}
              aria-label="Próximo banner"
              className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center bg-white/15 hover:bg-white/30 text-white rounded-full transition-colors"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            {/* Indicadores */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex">
              {banners.map((banner, index) => (
                <button
                  key={banner.id}
                  type="button"
                  onClick={() => setCurrentBannerIndex(index)}
                  aria-label={`Ir para o banner ${index + 1}`}
                  className="w-7 h-7 flex items-center justify-center"
                >
                  <span
                    className={cn(
                      "h-2 rounded-full transition-all duration-200",
                      index === currentBannerIndex
                        ? "w-5 bg-white"
                        : "w-2 bg-white/50"
                    )}
                  />
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};
